<template>
  <v-card flat>
    <v-card-title class="grey lighten-4 px-3 py-2">
      <v-layout row wrap>
        <v-flex sm6>
          <v-text-field append-icon="filter_list" :label="messages.label.BILLING_ID_FILTER" single-line hide-details
            clearable v-model="search" @input="value => filterSearchResult(value)">
          </v-text-field>
        </v-flex>
        <v-flex sm6 class="text-xs-right">
          <v-tooltip bottom>
            <v-btn slot="activator" flat @click="showInsertBillingIds()" color="primary">
              <v-icon left>add_circle</v-icon>
              <span>Insert</span>
            </v-btn>
            <span>Insert Billing Ids</span>
          </v-tooltip>
        </v-flex>
      </v-layout>
    </v-card-title>
    <v-card-text class="py-2">
      <v-data-table :headers="headers" :items="filteredSearchResult" item-key="id" :total-items="totalItems"
        hide-actions :loading="billingIdsLoading">
        <!-- Items -->
        <template slot="items" slot-scope="props">
          <tr>
            <td>
              <v-btn fab dark small dense color="primary" :disabled="billingIdsLoading"
                @click="showInsertBillingIds(props.item)">
                <v-icon dark>edit</v-icon>
              </v-btn>
            </td>
            <td class="text-xs-left">{{ props.item.affiliateName }}</td>
            <td class="text-xs-left">{{ props.item.count }}</td>
            <td class="text-xs-left py-2">
              <span v-html="getBillingIdRangeString(props.item)"></span>
            </td>
            <td>
              <v-alert class="py-2" v-if="props.item.count <= 10" :value="true" type="warning">
                Low on Billing Ids, please request for more
              </v-alert>
            </td>
          </tr>
        </template>
        <!-- No Data -->
        <template slot="no-data">
          <v-alert :value="true" color="info" class="black--text" icon="info">
            There are no billing Ids
          </v-alert>
        </template>
        <!-- No Results -->
        <template slot="no-results">
          <v-alert :value="true" color="info" class="black--text" icon="info">
            Sorry, there are no billing ids matching the filter criteria.
          </v-alert>
        </template>
      </v-data-table>
      <v-snackbar :timeout="snackbar.timeout" :top="true" color="light-green" v-model="snackbar.show">
        {{ snackbar.message }}
        <v-btn flat color="primary" @click.native="snackbar.show = false">Ok</v-btn>
      </v-snackbar>
    </v-card-text>
    <!-- Insert Billing Ids -->
    <v-dialog v-model="dialog" width="500">
      <v-card>
        <v-card-title class="headline font-weight-light">
          Insert Billing Ids
        </v-card-title>
        <v-card-text>
          <v-alert color="success" icon="check_circle" value="true" v-if="isInsertSuccess && insertResponseMsg !== ''">
            <span v-html="insertResponseMsg"></span>
          </v-alert>
          <v-alert color="error" icon="warning" value="true" v-if="!isInsertSuccess && insertResponseMsg !== ''">
            <span v-html="insertResponseMsg"></span>
          </v-alert>
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-layout wrap align-center>
              <v-flex xs12 d-flex class="pb-2">
                <v-select :items="affiliates" v-model="selectedAffiliate" item-value="name" item-text="name"
                  return-object required persistent-hint validate-on-blur :rules="affiliateRules" label="Affiliate">
                </v-select>
              </v-flex>
              <v-flex xs12 d-flex class="pb-2">
                <v-slide-x-transition>
                  <v-alert class="py-2 black--text" v-if="!$lodash.isEmpty(selectedAffiliate)" :value="true"
                    type="info">
                    {{ billingIdsCountHint }}
                  </v-alert>
                </v-slide-x-transition>
              </v-flex>
              <v-flex xs5 d-flex>
                <v-text-field v-model="billingIdStart" :rules="billingIdRules" :counter="5" label="Billing Id - Start"
                  required>
                </v-text-field>
              </v-flex>
              <v-flex xs2 d-flex>
              </v-flex>
              <v-flex xs5 d-flex>
                <v-text-field v-model="billingIdCount" :rules="countRules" :counter="10" mask="##########" label="Count"
                  required>
                </v-text-field>
              </v-flex>
            </v-layout>
          </v-form>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" v-if="isInsertSuccess" :loading="billingIdsLoading" :disabled="billingIdsLoading"
            @click="reloadBillingIds">
            Done
          </v-btn>
          <div v-else>
            <v-btn color="grey lighten-1 white--text" @click="cancelEdit">
              Cancel
            </v-btn>
            <v-btn color="primary" :loading="updatingBillingIds" :disabled="updatingBillingIds"
              @click="insertBillingIds">
              Save
            </v-btn>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import axios from "axios"
import { mapGetters } from "vuex"
import uniqid from "uniqid"
import AppService from "@/AppService"
import * as preIngestDataService from "@/PreIngest/PreIngestDataService"
import {
  VOD_SYCN_OFFERING_DATA_SERVICE_URL, VOD_SYCN_OFFERING_WEB_SERVICE_URL
} from "@/store/constants/getter-types"
import messages from "@/PreIngest/messages.json"

/**
 * initialData
 */
function initialData(instance) {
  return {
    search: "",
    dialog: false,
    valid: false,
    billingIdsLoading: false,
    updatingBillingIds: false,
    isInsertSuccess: false,
    snackbar: {
      show: false,
      message: "",
      timeout: 6000
    },
    totalItems: 0,
    headers: instance.getTableHeaders(),
    searchResult: [],
    filteredSearchResult: [],
    affiliates: instance.getAffiliates(),
    selectedAffiliate: {},
    billingIdStart: "",
    billingIdCount: "",
    insertResponseMsg: "",
    billingIdRules: [
      (v) => !!v || "Billing Id Start is required",
      (v) =>
        (v && v.length <= 5) || "Billing Id must be less than 5 characters",
      (v) => instance.isValidBillingId(v)
    ],
    countRules: [
      (v) => !!v || "Count is required",
      (v) => (v && v.length <= 10) || "That's too big"
    ],
    affiliateRules: [(v) => !!v || "Affiliate is required"]
  }
}

export default {
  name: "BillingIdsSearch",
  data: function () {
    return initialData(this)
  },
  computed: {
    ...mapGetters([
      VOD_SYCN_OFFERING_DATA_SERVICE_URL, VOD_SYCN_OFFERING_WEB_SERVICE_URL
    ]),
    showGridFilter() {
      return this.searchResult.length > 0
    },
    billingIdsCountHint() {
      let index = this.$lodash.findIndex(this.searchResult, (obj) => {
        return obj.affiliateName === this.selectedAffiliate.name
      })
      if (index > -1) {
        return "Available billing Ids: " + this.searchResult[index].count
      } else {
        return "Available billing Ids: n/a"
      }
    }
  },
  methods: {
    /**
     * reset
     */
    reset() {
      Object.assign(this.$data, initialData(this))
      this.getBillingIds()
    },

    /**
     * For filtering search result with text value
     */
    filterSearchResult(search) {
      if (search) {
        search = search.toString().toLowerCase()
        const displayedKeys = ["affiliateName", "count", "range"]
        this.filteredSearchResult = this.searchResult.filter((obj) => {
          let filteredObj = this.$lodash.pick(obj, displayedKeys)
          return Object.keys(filteredObj).some((key) => {
            if (key === "range") {
              let ranges = []
              let rangeString = ""
              this.$lodash.forEach(filteredObj[key], (rangeObj) => {
                ranges.push(rangeObj.title)
              })
              rangeString = ranges.join(",")
              let includes = rangeString.toLowerCase().includes(search)
              return includes
            } else {
              if (filteredObj[key] !== null) {
                return filteredObj[key]
                  .toString()
                  .toLowerCase()
                  .includes(search)
              }
            }
          })
        })
      } else {
        this.filteredSearchResult = this.searchResult
      }
    },

    /**
     * reloadBillingIds - description
     *
     * @return {type}  description
     */
    reloadBillingIds() {
      this.dialog = false
      this.getBillingIds()
    },

    /**
     * getBillingIds - description
     *
     * @return {type}  description
     */
    getBillingIds() {
      this.billingIdsLoading = true
      this.searchResult = []
      this.totalItems = this.affiliates.length
      this.$lodash.forEach(this.affiliates, (affiliate) => {
        axios
          .all([
            this.getBillingIdsCountForAffiliate(affiliate.name),
            this.getBillingIdRangesForAffiliate(affiliate.name)
          ])
          .then(
            axios.spread((count, range) => {
              let billingIdObj = {
                id: uniqid(),
                affiliateName: affiliate.name,
                count: count,
                range: range
              }
              this.searchResult.push(billingIdObj)
              this.billingIdsLoading = false
            })
          )
          .catch((error) => {
            this.billingIdsLoading = false
            console.log(error)
          })
      })
      this.filterSearchResult()
    },

    /**
     * getBillingIdsCountForAffiliate - description
     *
     * @param  {type} affiliateName description
     * @return {type}               description
     */
    getBillingIdsCountForAffiliate(affiliateName) {
      let queryObject = {
        byState: "AVAILABLE",
        byAffiliate: affiliateName,
        count: true,
        entries: false
      }
      return preIngestDataService.retrieveBillingIds(queryObject,
        this.VOD_SYCN_OFFERING_DATA_SERVICE_URL)
        .then((response) => {
          return response.data.totalResults
        })
        .catch((error) => {
          console.log(error)
          return Promise.reject(error)
        })
    },

    /**
     * getBillingIdRangesForAffiliate - description
     *
     * @param  {type} affiliateName description
     * @return {type}               description
     */
    getBillingIdRangesForAffiliate(affiliateName) {
      let queryObject = {
        byAffiliate: affiliateName,
        count: true,
        entries: true
      }
      return preIngestDataService.retrieveBillingIdsRange(queryObject,
        this.VOD_SYCN_OFFERING_DATA_SERVICE_URL)
        .then((response) => {
          return response.data.entries
        })
        .catch((error) => {
          console.log(error)
          return Promise.reject(error)
        })
    },

    /**
     * insertBillingIds - description
     *
     * @return {type}  description
     */
    insertBillingIds() {
      if (this.$refs.form.validate()) {
        this.updatingBillingIds = true
        let reqBody = {
          insertBillingIds: {
            affiliate: this.selectedAffiliate.name,
            startingBillingId: this.billingIdStart,
            totalBillingIds: this.billingIdCount
          }
        }
        preIngestDataService.insertBillingIds(reqBody,
          this.selectedAffiliate.accountId,
          this.VOD_SYCN_OFFERING_WEB_SERVICE_URL)
          .then(() => {
            this.updatingBillingIds = false
            this.isInsertSuccess = true
            //  let publishMetadataResponse = response.data.insertBillingIdsResponse
            this.insertResponseMsg = this.messages.info.INSERT_BILLING_ID_SUCCESS
          })
          .catch((error) => {
            this.updatingBillingIds = false
            this.isInsertSuccess = false
            let response = error.response.data
            if (response.isException && response.description) {
              this.insertResponseMsg = response.description
            } else {
              this.insertResponseMsg = this.messages.error.INSERT_BILLING_ID_FAILURE
            }
          })
      }
    },

    /**
     * getBillingIdRangeString - description
     *
     * @param  {type} billingIdObj description
     * @return {type}              description
     */
    getBillingIdRangeString(billingIdObj) {
      let ranges = []
      if (
        !this.$lodash.isUndefined(billingIdObj.range) &&
        billingIdObj.range.length > 0
      ) {
        this.$lodash.forEach(billingIdObj.range, (obj) => {
          ranges.push(`${obj.startingId} - ${obj.endingId}`)
        })
        return ranges.join("<br/>")
      } else {
        return "N/A"
      }
    },

    /**
     * getTableHeaders - Construct array of objects to show table headers
     *
     * @return {array}
     */
    getTableHeaders() {
      let headers = [
        {
          text: " ",
          sortable: false
        },
        {
          text: "Affiliate Name",
          align: "left",
          sortable: true,
          value: "affiliateName"
        },
        {
          text: "Available",
          align: "left",
          sortable: true,
          value: "count"
        },
        {
          text: "Billing ID Range(s)",
          align: "left",
          sortable: false,
          value: "range"
        },
        {
          text: "",
          sortable: false
        }
      ]

      return headers
    },

    /**
     * getAffiliates - description
     *
     * @return {type}  description
     */
    getAffiliates() {
      return [
        {
          id: 1,
          name: "Frontier",
          accountId: "http://access.auth.theplatform.com/data/Account/2706167635"
        }
      ]
    },

    /**
     * showInsertBillingIds - description
     *
     * @return {type}  description
     */
    showInsertBillingIds(billingIdObj) {
      this.$refs.form.reset()
      this.updatingBillingIds = false
      this.isInsertSuccess = false
      this.insertResponseMsg = ""
      this.valid = false
      if (billingIdObj) {
        this.selectedAffiliate = this.$lodash.find(this.getAffiliates(), { "name": billingIdObj.affiliateName })
      }
      this.dialog = true
    },

    /**
     * isValidBillingId - description
     *
     * @param  {type} val description
     * @return {type}     description
     */
    isValidBillingId(val) {
      var regEx = /^([a-zA-Z0-9]){5}$/
      let isValid = val && val.match(regEx)

      if (isValid) {
        return true
      } else {
        return "Invalid Biling Id"
      }
    },

    /**
     * cancelEdit - description
     *
     * @return {type}  description
     */
    cancelEdit() {
      this.$refs.form.reset()
      this.dialog = false
      this.valid = false
    }
  },
  created() {
    /**
     * Non reactive properties
     */
    this.messages = messages
    this.getBillingIds()
    AppService.$on("event:mpxAccountChanged", this.reset)
  },
  beforeDestroy() {
    AppService.$off("event:mpxAccountChanged", this.reset)
  }
}
</script>

<style scoped>
</style>



// WEBPACK FOOTER //
// src/PreIngest/BillingIds/BillingIdsSearch.vue